import Config from 'config';
import { call, fork, put, select } from 'redux-saga/effects';
import waitForSession from '../../../lib/wait-for-session';

const qs = require('qs');
const _ = require('lodash');

import {
  getCustomReportSelected,
  getReportSelected,
  getReportTimeRange,
  getCustomSelectedEvents,
  getCustomSelectedIDsFromModal,
} from '../getters';

import {
  gotoReportsPage,
} from '../../../lib/navigate-to';

import { getUserId } from '../../session/getters';
import { getValidator, getFilterValidator } from '../../../modules/reports/getters';

import {
  ON_REPORTS_MOUNT_ERROR,
  REPORTS_DOWNLOAD_QUICK_REPORT_SUCCESS,
  REPORTS_DOWNLOAD_QUICK_REPORT_ERROR,
  CLOSE_CUSTOM_REPORT_DELETE_MODAL,
  ON_REPORTS_SAVE_SUCCESS,
  ON_REPORTS_SAVE_ERROR,
  ON_REPORTS_DOWNLOAD_SUCCESS,
  ON_REPORTS_DOWNLOAD_ERROR,
  ON_VALIDATE_FORM_STARTED,
  ON_VALIDATE_FORM_SUCCESS,
  ON_VALIDATE_FORM_ERROR,
  ON_REPORTS_SET_CUSTOM_EVENTS,
  ON_REPORTS_DELETE_SUCCESS,
  OPEN_LARGE_REPORT_EMAIL_MODAL,
  UPDATE_CUSTOM_REPORTS_PAGINATION
} from '../actions';

// Action Creators
const saveReportActionCreator = reportID => {
  return {
    type: ON_REPORTS_SAVE_SUCCESS,
    payload: reportID
  };
};

const downloadReportActionCreator = () => ({
  type: ON_REPORTS_DOWNLOAD_SUCCESS
});

const deleteReportCreator = () => ({
  type: ON_REPORTS_DELETE_SUCCESS
});

// Mounting/Init
export function* onReportsMount() {
  yield call(waitForSession);
  const userId = yield select(getUserId);
  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }
  }
  catch (e) {
    yield put({ type: ON_REPORTS_MOUNT_ERROR });
  }
}

// CRUD helpers
function saveReportHelper(data) {
  // Pull out both mutation functions from data.payload
  const createReport = data.payload.createReport;
  const updateReport = data.payload.updateReport;

  // Pull out report data from data.payload
  const {
    id,
    requestType,
    type,
    name,
    description,
    columns,
    order
  } = data.payload.customGeneral;
  const scheduler = data.payload.customScheduler;
  const filters = data.payload.customFilters;
  const report_type = type.value; // eslint-disable-line

  const _listings = data.payload.selectedEventIDs;
  const _columns = parseColumns(columns, { mapOver: true, commaSeparatedString: false });
  const _filters = parseFilters(filters);
  const _scheduler = parseScheduler(scheduler);
  const _order = parseOrder(order);
  const timeFilter = data.payload.eventFilter.selectedTime;
  const eventRange = data.payload.eventFilter.dateFilter;
  const currencyFilter = data.payload.eventFilter.selectedCurrency;

  // Either create brand new report or update existing report
  return runMutation(requestType, createReport, updateReport, id, name, report_type, _columns, _filters, _listings, description, _scheduler, _order, timeFilter, eventRange, currencyFilter);
}

function downloadCustomReportHelper(data, hostID, fileType) {
  // Pull out report data from data.payload
  const {
    reportType,
    filters,
    columns,
    events,
    order,
    name,
  } = data.payload.report;

  const _columns = parseColumns(columns, { mapOver: false, commaSeparatedString: true });
  const _filters = parseFilters(filters);

  // Query for the Gila Report
  const queryObject = {
    report_type: reportType,
    columns: _columns,
    filters: _filters,
    listingIds: events,
    fileType,
    hostID,
    order,
    report_title: name,
  };

  window.location.href = generateCustomReportDownloadQuery(queryObject);
  return true;
}

function deleteReportHelper(data) {
  const { deleteMutation, gqlQueryObj, id } = data.payload;

  return deleteMutation({
    id
  }).then((d) => {
    if (!d.data.deleteCustomReport.errors){
      return gqlQueryObj.refetch();
    }
  });
}

function* tryDownloadReport(userId, value, fileFormat, timeRange) {
  try {
    const urlString = (value == 'activity_report') ?
    `${Config.api.host}/api/v2/users/${userId}/activity_report.${fileFormat}` :
    `${Config.api.host}/user_reports/${userId}/${value}.${fileFormat}${timeRange}&gila`;
    // sending Head request to see if status is 202 or 204 first
    // This way the report is only built once when we're ready to get it
    const result = yield fetch(urlString, { method: 'HEAD' });
    switch (result.status) {
      // 204 - "No Content"
      case 204:
      case 200: {
        yield put({ type: REPORTS_DOWNLOAD_QUICK_REPORT_SUCCESS });
        window.location.href = urlString;
        break;
      }
      // 202 - "Accepted for Processing"
      case 202: {
        yield put({ type: OPEN_LARGE_REPORT_EMAIL_MODAL });
        break;
      }
    }
  }
  catch (e) {
    yield put({ type: ON_REPORTS_DOWNLOAD_ERROR });
  }
}

function* validateForm() {
  // Start validate action
  yield put({ type: ON_VALIDATE_FORM_STARTED });

  try {
    const validator = yield select(getValidator);
    const filterValidator = yield select(getFilterValidator);
    if (validator.valid && filterValidator.valid){
      yield put({ type: ON_VALIDATE_FORM_SUCCESS });
      return true;
    }
    throw (ON_VALIDATE_FORM_ERROR);
  }
  catch (e) {
    yield put({ type: e });
  }
}

export function* saveReport(data) {
  yield call(waitForSession);
  const userId = yield select(getUserId);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }
    // Validate the form to see if there's anything missing
    const isValid = yield call(validateForm);

    if (isValid){
      // If form is valid, start saving and downloading process
      yield call(requestAndPut, [saveReportHelper, data], saveReportActionCreator);
      yield call(gotoReportsPage);
    }
    else {
      // If form is invalid, bail out.
      yield put({ type: ON_VALIDATION_ERROR });
    }
  }
  catch (e) {
    yield put({ type: ON_REPORTS_SAVE_ERROR });
  }
}

export function* downloadCustomReport(data) {
  yield call(waitForSession);
  const userId = yield select(getUserId);
  const { fileType } = yield select(getCustomReportSelected);

  try {
    if (!userId) {
      throw new Error('No user - aborting');
    }

    yield fork(requestAndPut, [downloadCustomReportHelper, data, userId, fileType], downloadReportActionCreator);
  }
  catch (e) {
    yield put({ type: ON_REPORTS_DOWNLOAD_ERROR });
  }
}

export function* downloadQuickReport() {
  const userId = yield select(getUserId);
  const { value, fileFormat } = yield select(getReportSelected);
  const timeRange = yield select(getReportTimeRange) || '';
  const mumHost = JSON.parse(localStorage.getItem('mumHost'));
  const hostId = mumHost ? mumHost.id : userId;

  try {
    yield call(tryDownloadReport, hostId, value, fileFormat.toLowerCase(), timeRange);
  }
  catch (e) {
    yield put({ type: REPORTS_DOWNLOAD_QUICK_REPORT_ERROR });
  }
}

export function* deleteReport(data) {
  yield call(waitForSession);
  yield call(requestAndPut, [deleteReportHelper, data], deleteReportCreator);
  yield put({ type: CLOSE_CUSTOM_REPORT_DELETE_MODAL });
  const { limit, offset, currentPage } = data.payload;
  yield put({ type: UPDATE_CUSTOM_REPORTS_PAGINATION, payload: { limit, offset, currentPage }});
}

// EventSelect modal
export function* setCustomEvents() {
  const ids = yield select(getCustomSelectedIDsFromModal);
  const events = yield select(getCustomSelectedEvents);
  yield put({ type: ON_REPORTS_SET_CUSTOM_EVENTS, payload: { events, ids }});
}

// Misc
export function* redirectToHomepage() {
  window.location.href = `${Config.api.host}`;
  return false;
}

export function* requestAndPut(requestParameters, actionCreator) {
  const result = yield call(...requestParameters);
  yield put(actionCreator(result));
}

function parseColumns(c, opts){
  let _columns = c;

  if (opts.mapOver === true){
    _columns = c.map(x => {
      return x.column;
    });
  }

  if (opts.commaSeparatedString === true)
    _columns = _columns.join(',');

  return _columns;
}

function parseFilters(f){
  // Parse filters into array of objects with only the relevant
  //  information from filters
  const _filters = [];

  // below code handles a case where the provided comparator is '...'
  // since web only accepts > >= < <= = != comparators,
  // break a range comparator into less than / greater than
  Object.keys(f).forEach(e => {
    if (f[e]){
      if (f[e].comparator === '...') {
        f[e].value.split(',').map((value, index) => {
          _filters.push({
            column: f[e].column,
            value,
            comparator: index == 0 ? '>=' : '<='
          });
        });
      }
      else {
        const _obj = {
          column: f[e].column,
          value: f[e].value,
          comparator: f[e].comparator,
        };

        _filters.push(_obj);
      }
    }
  });

  return _filters;
}

function parseScheduler(scheduler) {
  const enabled = scheduler.enabled;
  const isDaily = scheduler.frequency.selected.value === 'DAILY';
  let _scheduler = {};

  if (!enabled) {
    _scheduler = {
      from: null,
      to: null,
      days: [],
      hour: null,
      enabled
    };
  }
  else {
    _scheduler = {
      from: scheduler.dateRange.startDate,
      to: scheduler.dateRange.endDate,
      days: isDaily ? scheduler.time.dayOptions.map(day => day.value) : [scheduler.time.day.value],
      hour: scheduler.time.hour.value,
      enabled
    };
  }

  return _scheduler;
}

function parseOrder(ob) {
  return ob.split('__').join(' ');
}

function runMutation(requestType, createReport, updateReport, id, n, rt, c, f, l, d, scheduler, ob, timeFilter, eventRange, currencyFilter) {
  // Runs the mutation exported from the view layer
  switch (requestType){
    default:{
      return false;
    }
    case 'POST':{
      return createReport({
        name: n,
        description: d,
        reportType: rt,
        columns: c,
        filters: f,
        events: l,
        from: scheduler.from,
        to: scheduler.to,
        days: scheduler.days,
        hour: scheduler.hour,
        enabled: scheduler.enabled,
        order: ob,
        timeFilter,
        eventRangeFrom: eventRange.from,
        eventRangeTo: eventRange.to,
        currencyFilter
      }).then(res => {
        return res.data.createCustomReport.custom_report.id;
      });
      break;
    }
    case 'PUT':{
      return updateReport({
        id,
        name: n,
        description: d,
        reportType: rt,
        columns: c,
        filters: f,
        events: l,
        from: scheduler.from,
        to: scheduler.to,
        days: scheduler.days,
        hour: scheduler.hour,
        enabled: scheduler.enabled,
        order: ob,
        timeFilter,
        eventRangeFrom: eventRange.from,
        eventRangeTo: eventRange.to,
        currencyFilter
      }).then(() => {
        return true; // don't need to pass through the new reportID, so pass true instead
      });
      break;
    }
  }
}

function generateCustomReportDownloadQuery({ report_type, columns, filters, listingIds, fileType, hostID, order, report_title }) {
  const reportObject = {
    columns,
    filters,
    report_type,
    order,
    report_title,
  };

  // 'listings' might be an empty array, in which case don't include
  if (!_.isEmpty(listingIds)) reportObject.listing_ids = listingIds;

  const query = qs.stringify(reportObject, { arrayFormat: 'brackets' });

  return `${Config.api.host}/user_reports/${hostID}/custom.${fileType}?${query}&gila`;
}

export function redirectToSignIn() {
  window.location.href = `${Config.api.host}/sign-in?redirectTo=dashboard/reports`;
}



// WEBPACK FOOTER //
// ./src/modules/reports/actions/sagas.js